import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { withUser } from "../Contexts/user";
import queries from "../helpers/queries";
import caseQueries from "../helpers/caseQueries";
import { useMutation, useApolloClient } from "@apollo/react-hooks";
import {
    FiFileText,
} from "react-icons/fi";
import moment from "moment";
import Modal from "../Components/modal";
import propertyTypes from "../propertyTypes";

let timerId;
let projectTimerId;
let agencyTimerId;

const NewCase = ({ location, user }) => {
    const history = useHistory();
    const { register, handleSubmit, control, setValue, getValues, errors } =
        useForm();
    const [activeTab, setActiveTab] = useState("caseInfo");
    const [showTemplate, setShowTemplate] = useState(false);
    const apollo = useApolloClient();

    // Add state for managing lawyer firms only
    const [lawyerFirms, setLawyerFirms] = useState([""]);

    // Add functions to handle lawyer firms array
    const addLawyerFirm = () => {
        setLawyerFirms([...lawyerFirms, ""]);
    };

    const removeLawyerFirm = (index) => {
        setLawyerFirms(lawyerFirms.filter((_, i) => i !== index));
    };

    const updateLawyerFirm = (index, value) => {
        const newLawyerFirms = [...lawyerFirms];
        newLawyerFirms[index] = value;
        setLawyerFirms(newLawyerFirms);
    };

    const loadActiveUsers = useCallback(
        (val, callback) => {
            clearTimeout(timerId);
            timerId = setTimeout(async () => {
                const result = await apollo.query({
                    query: queries.FETCH_USERS,
                    variables: {
                        name: val,
                        limit: 9999,
                        page: 1,
                        type: "user",
                    },
                    fetchPolicy: "network-only",
                });

                callback([
                    {
                        label: "SELF",
                        value: user.auth.userId
                    },
                    ...result.data.fetchUsers.data.map((item) => ({
                        label: item.name,
                        value: item._id,
                    }))
                ]);
            }, 500);
        },
        [apollo, user.auth.userId]
    );

    const loadProjects = useCallback(
        (val, callback) => {
            clearTimeout(projectTimerId);
            projectTimerId = setTimeout(async () => {
                const result = await apollo.query({
                    query: caseQueries.FETCH_PROJECTS,
                    variables: {
                        limit: 9999,
                        page: 1,
                        name: val,
                    },
                    fetchPolicy: "network-only",
                });

                callback(
                    result.data.fetchProjects.data.map((item) => ({
                        label: item.name,
                        value: item._id,
                    }))
                );
            }, 500);
        },
        [apollo]
    );

    const loadAgencies = useCallback(
        (val, callback) => {
            clearTimeout(agencyTimerId);
            agencyTimerId = setTimeout(async () => {
                const result = await apollo.query({
                    query: caseQueries.FETCH_AGENCIES,
                    variables: {
                        name: val,
                        status: "active",
                        limit: 9999,
                        page: 1,
                        pagination: {
                            limit: 9999,
                            page: 1,
                        },
                    },
                    fetchPolicy: "network-only",
                });

                callback(
                    result.data.fetchAgencies.data.map((item) => ({
                        label: item.name,
                        value: item._id,
                    }))
                );
            }, 500);
        },
        [apollo]
    );

    // Form field definitions
    const createformFields = [
        {
            name: "salesType",
            label: "Sales Type (Case)",
            type: "select",
            options: [
                { value: "project", label: "PROJECT" },
                { value: "subsale", label: "SUBSALE" },
                { value: "auction", label: "AUCTION" },
                { value: "refinance", label: "REFINANCE" },
            ],
            section: "Case Information",
        },
        {
            name: "applyFor",
            label: "Apply For",
            type: "select",
            options: [
                { value: "conventional", label: "Conventional" },
                { value: "islamic", label: "Islamic" },
            ],
            section: "Case Information",
        },
        {
            name: "flexiType",
            label: "Flexi Type",
            type: "select",
            options: [
                { value: "full", label: "Fully Flexi" },
                { value: "semi", label: "Semi Flexi" },
            ],
            section: "Case Information",
        },
        // Property Loan Details Section
        {
            name: "propertyAgent",
            label: "Property Agent",
            section: "Property Loan Details",
        },
        {
            name: "unitNumber",
            label: "Unit Number",
            section: "Property Loan Details",
        },
        {
            name: "propertyAddress",
            label: "Full Property Address",
            section: "Property Loan Details",
            type: "textarea",
        },
        {
            name: "propertyRenodetails",
            label: "Renovation Details",
            type: "textarea",
            section: "Property Loan Details",
        },
        {
            name: "propertyType",
            label: "Property Type",
            type: "select",
            options: propertyTypes,
            section: "Property Loan Details",
            isSearchable: true,
        },
        {
            name: "completionStatus",
            label: "Completed / Under con (For Dev Case Only)",
            type: "select",
            options: [
                { value: "completed", label: "COMPLETED" },
                { value: "underCon", label: "UNDER CON" },
            ],
            section: "Property Loan Details",
        },
        {
            name: "bumiStatus",
            label: "Bumi / Non Bumi",
            type: "select",
            options: [
                { value: "bumi", label: "BUMI" },
                { value: "nonbumi", label: "NON BUMI" },
                { value: "malay", label: "MALAY RESERVED LAND" },
            ],
            section: "Property Loan Details",
        },
        {
            name: "propertyHold",
            label: "Property Hold",
            type: "select",
            options: [
                { value: "freehold", label: "FREEHOLD" },
                { value: "leasehold", label: "LEASEHOLD" },
            ],
            section: "Property Loan Details",
        },
        {
            name: "propertySize",
            label: "Build Up Size (SQFT)",
            section: "Property Loan Details",
        },
        {
            name: "landsize",
            label: "Land Size (SQFT)",
            section: "Property Loan Details",
        },
        {
            name: "spaPrice",
            label: "SPA Price (RM)",
            type: "number",
            step: "0.01",
            section: "Property Loan Details",
        },
        {
            name: "marginRequested",
            label: "Margin Requested (%)",
            type: "string",
            section: "Property Loan Details",
        },
        {
            name: "loanAmount",
            label: "Loan Amount (RM)",
            type: "number",
            step: "0.01",
            section: "Property Loan Details",
        },
        {
            name: "loanTenure",
            label: "Loan Tenure (YEARS)",
            type: "number",
            section: "Property Loan Details",
        },
        // Lawyer Firm Section
        {
            name: "spaName",
            label: "SPA Name(Borrower)",
            section: "Lawyer Firm",
            type: "dynamicArray",
            required: true,
        },
        {
            name: "spaLawyer",
            label: "SPA Lawyer Firm",
            section: "Lawyer Firm",
        },
        {
            name: "laLawyer",
            label: "LA Lawyer Firm",
            section: "Lawyer Firm",
        },
       
        {
            name: "valuerFirm",
            label: "Valuation Firm",
            section: "Lawyer Firm",
        },

        {
            name: "valuerName",
            label: "Valuer Name",
            section: "Lawyer Firm",
        },

        {
            name: "marketValue",
            label: "Market Value (RM)",
            type: "number",
            step: "0.01",
            section: "Lawyer Firm",
        },
        {
            name: "remarks",
            label: "Remarks",
            section: "Remarks",
            type: "textarea",
        },
        // Person In Charge section
        {
            name: "agency",
            label: "Agency",
            type: "asyncselect",
            section: "Person In Charge",
            loadOptions: "loadAgencies",
        },
        {
            name: "project",
            label: "Project",
            type: "asyncselect",
            section: "Person In Charge",
            loadOptions: "loadProjects",
        },
        // Emergency Contact section
        {
            name: "emergencyName",
            label: "Full Name",
            section: "Emergency Contact",
            required: true,
            helperText: "(Address Can't Same With Applicant Above)",
        },
        {
            name: "emergencyIC",
            label: "IC",
            section: "Emergency Contact",
            required: true,
        },
        {
            name: "emergencyContact",
            label: "Contact",
            section: "Emergency Contact",
            required: true,
        },
        {
            name: "emergencyOccupation",
            label: "Occupation",
            section: "Emergency Contact",
            required: true,
        },
        {
            name: "emergencyRelationship",
            label: "Relationship",
            section: "Emergency Contact",
            required: true,
        },
        {
            name: "emergencyAddress",
            label: "Address",
            section: "Emergency Contact",
            required: true,
            type: "textarea",
        },
        // Borrower Information section
        {
            name: "borrowerSalary",
            label: "Salary (RM)",
            type: "number",
            step: "0.01",
            section: "Borrower Information",
            required: true,
        },
        {
            name: "borrowerName",
            label: "Borrower Name",
            section: "Borrower Information",
        },
        {
            name: "borrowerNric",
            label: "Borrower NRIC",
            section: "Borrower Information",
        },
        {
            name: "borrowerContact",
            label: "Borrower Contact",
            section: "Borrower Information",
        },
        {
            name: "borrowerEmail",
            label: "Borrower Email",
            section: "Borrower Information",
        },
        {
            name: "borrowerEducationLevel",
            label: "Education Level",
            section: "Borrower Information",
        },
        {
            name: "borrowerSmoking",
            label: "Smoking Status",
            type: "select",
            options: [
                { value: "yes", label: "Yes" },
                { value: "no", label: "No" },
            ],
            section: "Borrower Information",
        },
        {
            name: "borrowerMotherName",
            label: "Mother's Name",
            section: "Borrower Information",
        },
        {
            name: "borrowerLengthOfStay",
            label: "Length of Stay (Years)",
            section: "Borrower Information",
        },
        {
            name: "borrowerMailingAddress",
            label: "Mailing Address",
            type: "textarea",
            section: "Borrower Information",
        },
        {
            name: "borrowerAllDetails",
            label: "All Details",
            type: "textarea",
            section: "Borrower Information",
        },
        {
            name: "borrowerMaritalStatus",
            label: "Marital Status",
            type: "select",
            options: [
                { value: "married", label: "Married" },
                { value: "single", label: "Single" },
                { value: "divorced", label: "Divorced" },
                { value: "widow", label: "Widow" },
            ],
            section: "Borrower Information",
        },
        {
            name: "spouseName",
            label: "Spouse's Name",
            section: "Borrower Information",
        },
        {
            name: "spouseNRIC",
            label: "Spouse's NRIC",
            section: "Borrower Information",
        },
        {
            name: "spouseContact",
            label: "Spouse's Contact",
            section: "Borrower Information",
        },
        {
            name: "spouseOccupation",
            label: "Spouse's Occupation",
            section: "Borrower Information",
        },
        {
            name: "spouseCompanyName",
            label: "Spouse's Company Name",
            section: "Borrower Information",
        },
        {
            name: "spouseMonthlyIncome",
            label: "Spouse's Monthly Income (RM)",
            type: "number",
            step: "0.01",
            section: "Borrower Information",
        },
        {
            name: "childrensNo",
            label: "No. of Children",
            section: "Borrower Information",
        },
        
        {
            name: "borrowerCurrentCompanyName",
            label: "Current Company Name",
            section: "Current Employment Details",
        },
        {
            name: "borrowerCurrentCompanyAddress",
            label: "Current Company Address",
            type: "textarea",
            section: "Current Employment Details",
        },
        {
            name: "borrowerCurrentHRDirectline",
            label: "Current HR Directline",
            section: "Current Employment Details",
        },
        {
            name: "borrowerPosition",
            label: "Current Position",
            section: "Current Employment Details",
        },
        {
            name: "currentLengthOfService",
            label: "Current Date of Joining",
            section: "Current Employment Details",
        },
        {
            name: "borrowerCurrentNatureBusiness",
            label: "Current Nature of Business",
            section: "Current Employment Details",
        },
       
        // Previous Employment Status

        {
            name: "borrowerPastCompanyName",
            label: "Past Company Name",
            section: "Previous Employment Status",
            helperText:
                "(If Working At Current Company Less Than 1 or 2 Years)",
        },
        {
            name: "borrowerPastCompanyAddress",
            label: "Past Company Address",
            section: "Previous Employment Status",
            type: "textarea",
        },
        {
            name: "borrowerPastHRDirectline",
            label: "Past HR Directline",
            section: "Previous Employment Status",
        },
        {
            name: "borrowerPastPosition",
            label: "Past Position",
            section: "Previous Employment Status",
        },
        {
            name: "borrowerPastYearService",
            label: "Past Length of Service",
            section: "Previous Employment Status",
        },
        {
            name: "borrowerPastNatureBusiness",
            label: "Past Nature of Business",
            section: "Previous Employment Status",
        },
      
    ];

    // Mutation for creating a case
    const [createCase] = useMutation(caseQueries.CREATE_CASE);

    // Add state for autofill modal
    const [createNoteForm, setCreateNoteForm] = useState(false);
    const [draftNoteForm, setDraftNoteForm] = useState(false);

    // Add this state for the textarea value
    const [noteContent, setNoteContent] = useState("");
    const [draftNoteContent, setDraftNoteContent] = useState("");
    const [isAllDetailsMode, setIsAllDetailsMode] = useState(false);

    // Add new state near the top with other states
    const [borrowerNoteForm, setBorrowerNoteForm] = useState(false);
    const [borrowerNoteContent, setBorrowerNoteContent] = useState("");

    // Add these states near the top with other states
    const [borrowers, setBorrowers] = useState([{ id: 1 }]); // Initialize with one borrower
    const [selectedBorrower, setSelectedBorrower] = useState(1); // Track active borrower

    // Add this state to store borrower data
    const [borrowerData, setBorrowerData] = useState({
        1: {}, // Initialize with empty data for first borrower
    });

    useEffect(() => {
        // Set default case selections on initial load.
        if (!getValues("salesType")) {
            setValue("salesType", { value: "project", label: "PROJECT" });
        }
        if (!getValues("applyFor")) {
            setValue("applyFor", { value: "islamic", label: "Islamic" });
        }
        if (!getValues("flexiType")) {
            setValue("flexiType", { value: "semi", label: "Semi Flexi" });
        }
        if (!getValues("spaLawyer")) {
            setValue("spaLawyer", "TBC");
        }
        if (!getValues("laLawyer")) {
            setValue("laLawyer", "TBC");
        }
        if (!getValues("mdgAgent")) {
            setValue("mdgAgent", {
                value: "687093154b2c8180e89b5441",
                label: "Felix Chew",
            });
        }
    }, [getValues, setValue]);

    // Add this function to update borrower data when fields change
    const updateBorrowerData = (borrowerId, data) => {
        setBorrowerData((prev) => ({
            ...prev,
            [borrowerId]: {
                ...prev[borrowerId],
                ...data,
            },
        }));
    };

    const customStyles = useMemo(
        () => ({
            control: (provided, state) => ({
                ...provided,
                borderColor: "#e2e8f0",
                backgroundColor: "#fbfdfe",
                borderRadius: "0.5rem",
                padding: "0.20rem",
                "&:hover": {
                    borderColor: "#e2e8f0",
                },
            }),

            placeholder: (provided, state) => ({
                ...provided,
                color: "#a0aec0",
                fontSize: "0.75rem",
                fontWeight: "600",
            }),

            container: (provided, state) => ({
                ...provided,
                color: "#64748b",
                fontSize: "0.75rem",
                fontWeight: "600",
            }),
        }),
        []
    );

    // Add reset function for the modal
    const resetCreateNoteForm = () => {
        setCreateNoteForm(false);
    };

    const resetDraftNoteForm = () => {
        setDraftNoteForm(false);
    };

    // Add reset function with other reset functions
    const resetBorrowerNoteForm = () => {
        setBorrowerNoteForm(false);
    };

    // Add function to save current borrower data before switching
    const saveCurrentBorrowerData = () => {
        const formValues = getValues();
        const prefix = "";

        const currentData = {
            borrowerName: formValues[`${prefix}borrowerName_${selectedBorrower}`],
            borrowerNric: formValues[`${prefix}borrowerNric_${selectedBorrower}`],
            borrowerContact: formValues[`${prefix}borrowerContact_${selectedBorrower}`],
            borrowerEmail: formValues[`${prefix}borrowerEmail_${selectedBorrower}`],
            borrowerEducationLevel: formValues[`${prefix}borrowerEducationLevel_${selectedBorrower}`],
            borrowerSmoking: formValues[`${prefix}borrowerSmoking_${selectedBorrower}`],
            borrowerMotherName: formValues[`${prefix}borrowerMotherName_${selectedBorrower}`],
            borrowerLengthOfStay: formValues[`${prefix}borrowerLengthOfStay_${selectedBorrower}`],
            borrowerMailingAddress: formValues[`${prefix}borrowerMailingAddress_${selectedBorrower}`],
            borrowerAllDetails: formValues[`${prefix}borrowerAllDetails_${selectedBorrower}`],
            borrowerSalary: formValues[`${prefix}borrowerSalary_${selectedBorrower}`],
            borrowerMaritalStatus: formValues[`${prefix}borrowerMaritalStatus_${selectedBorrower}`],
            spouseName: formValues[`${prefix}spouseName_${selectedBorrower}`],
            spouseNRIC: formValues[`${prefix}spouseNRIC_${selectedBorrower}`],
            spouseContact: formValues[`${prefix}spouseContact_${selectedBorrower}`],
            spouseOccupation: formValues[`${prefix}spouseOccupation_${selectedBorrower}`],
            spouseCompanyName: formValues[`${prefix}spouseCompanyName_${selectedBorrower}`],
            spouseMonthlyIncome: formValues[`${prefix}spouseMonthlyIncome_${selectedBorrower}`],
            childrensNo: formValues[`${prefix}childrensNo_${selectedBorrower}`],
            currentLengthOfService: formValues[`${prefix}currentLengthOfService_${selectedBorrower}`],
            borrowerCurrentCompanyName: formValues[`${prefix}borrowerCurrentCompanyName_${selectedBorrower}`],
            borrowerCurrentCompanyAddress: formValues[`${prefix}borrowerCurrentCompanyAddress_${selectedBorrower}`],
            borrowerCurrentNatureBusiness: formValues[`${prefix}borrowerCurrentNatureBusiness_${selectedBorrower}`],
            borrowerCurrentHRDirectline: formValues[`${prefix}borrowerCurrentHRDirectline_${selectedBorrower}`],
            borrowerPosition: formValues[`${prefix}borrowerPosition_${selectedBorrower}`],
            borrowerPastCompanyName: formValues[`${prefix}borrowerPastCompanyName_${selectedBorrower}`],
            borrowerPastCompanyAddress: formValues[`${prefix}borrowerPastCompanyAddress_${selectedBorrower}`],
            borrowerPastNatureBusiness: formValues[`${prefix}borrowerPastNatureBusiness_${selectedBorrower}`],
            borrowerPastHRDirectline: formValues[`${prefix}borrowerPastHRDirectline_${selectedBorrower}`],
            borrowerPastPosition: formValues[`${prefix}borrowerPastPosition_${selectedBorrower}`],
            borrowerPastYearService: formValues[`${prefix}borrowerPastYearService_${selectedBorrower}`],
            emergencyName: formValues[`emergencyName_${selectedBorrower}`],
            emergencyContact: formValues[`emergencyContact_${selectedBorrower}`],
            emergencyRelationship: formValues[`emergencyRelationship_${selectedBorrower}`],
            emergencyAddress: formValues[`emergencyAddress_${selectedBorrower}`],
            emergencyIC: formValues[`emergencyIC_${selectedBorrower}`],
            emergencyOccupation: formValues[`emergencyOccupation_${selectedBorrower}`],
        };

        setBorrowerData((prev) => ({
            ...prev,
            [selectedBorrower]: currentData,
        }));
    };

    // Update borrower tab click handler
    const handleBorrowerTabClick = (borrowerId) => {
        saveCurrentBorrowerData();
        setSelectedBorrower(borrowerId);
    };

    // Update handleAddBorrower to save current data before adding new borrower
    const handleAddBorrower = () => {
        saveCurrentBorrowerData();
        const newBorrowerId = Math.max(...borrowers.map((b) => b.id)) + 1;
        setBorrowers([...borrowers, { id: newBorrowerId }]);
        setSelectedBorrower(newBorrowerId);
        setBorrowerData((prev) => ({
            ...prev,
            [newBorrowerId]: {},
        }));
    };

    // Update handleRemoveBorrower to save current data before removing
    const handleRemoveBorrower = (borrowerId) => {
        if (borrowers.length === 1) return;
        saveCurrentBorrowerData();
        const newBorrowers = borrowers.filter((b) => b.id !== borrowerId);
        setBorrowers(newBorrowers);
        setSelectedBorrower(newBorrowers[0].id);
        setBorrowerData((prev) => {
            const newData = { ...prev };
            delete newData[borrowerId];
            return newData;
        });
    };

    // Modify handleSubmitBorrowerNoteForm to update borrowerData
    const handleSubmitBorrowerNoteForm = () => {
        const lines = borrowerNoteContent.split("\n");
        const pairValues = [];

        lines.forEach((line) => {
            const [label, value] = line.split(":");
            if (label && value) {
                pairValues.push({
                    label: label.trim(),
                    value: value.trim(),
                });
            }
        });

        setBorrowerNoteForm(false);
        setBorrowerNoteContent("");

        if (pairValues.length > 0) {
            const newBorrowerData = {};

            // Create a mapping between note labels and form field names
            const fieldMapping = {
                "NAME": "borrowerName",
                "NRIC": "borrowerNric",
                "CONTACT": "borrowerContact",
                "EMAIL ADDRESS": "borrowerEmail",
                "EDUCATION LEVEL": "borrowerEducationLevel",
                "SMOKING/NO": "borrowerSmoking",
                "MOTHER NAME": "borrowerMotherName",
                "MAILING ADDRESS": "borrowerMailingAddress",
                "ALL DETAILS": "borrowerAllDetails",
                "LENGTH OF STAY In this MAILING ADDRESS": "borrowerLengthOfStay",
                "CURRENT COMPANY NAME": "borrowerCurrentCompanyName",
                "CURRENT COMPANY ADDRESS": "borrowerCurrentCompanyAddress",
                "CURRENT COMPANY TELEPHONE": "borrowerCurrentHRDirectline",
                "CURRENT POSITION": "borrowerPosition",
                "CURRENT DATE OF JOINING": "currentLengthOfService",
                "CURRENT NATURE OF BUSINESS": "borrowerCurrentNatureBusiness",
                "PREVIOUS COMPANY NAME": "borrowerPastCompanyName",
                "PREVIOUS ADDRESS": "borrowerPastCompanyAddress",
                "PREVIOUS NATURE OF BUSINESS": "borrowerPastNatureBusiness",
                "PREVIOUS HR DIRECT LINE": "borrowerPastHRDirectline",
                "PREVIOUS YRS IN SERVICE": "borrowerPastYearService",
                "PREVIOUS POSITION": "borrowerPastPosition",
                "MARITAL STATUS": "borrowerMaritalStatus",
                "SALARY": "borrowerSalary",
                "SPOUSE NAME": "spouseName",
                "SPOUSE IC": "spouseNRIC",
                "SPOUSE CONTACT": "spouseContact",
                "SPOUSE OCCUPATION": "spouseOccupation",
                "SPOUSE COMPANY NAME": "spouseCompanyName",
                "SPOUSE MONTHLY INCOME": "spouseMonthlyIncome",
                "NO. CHILDREN": "childrensNo",
                "FULL NAME": "emergencyName",
                "IC NUMBER": "emergencyIC",
                "EMERGENCY CONTACT": "emergencyContact",
                "RELATIONSHIP": "emergencyRelationship",
                "HOUSE ADDRESS": "emergencyAddress",
                "OCCUPATION": "emergencyOccupation",
            };

            pairValues.forEach((pair) => {
                const baseFieldName = fieldMapping[pair.label];
                if (baseFieldName) {
                    const fieldName = `${baseFieldName}_${selectedBorrower}`;
                    let value = pair.value;

                    // Handle special cases
                    if (baseFieldName === "borrowerSmoking") {
                        value = {
                            label:
                                pair.value.toLowerCase() === "no"
                                    ? "NO"
                                    : "YES",
                            value:
                                pair.value.toLowerCase() === "no"
                                    ? "no"
                                    : "yes",
                        };
                    } else if (baseFieldName === "borrowerMaritalStatus") {
                        value = {
                            label: pair.value.charAt(0).toUpperCase() + pair.value.slice(1),
                            value: pair.value.toLowerCase(),
                        };
                    }

                    // Update form field
                    setValue(fieldName, value);

                    // Store in borrowerData without the ID suffix
                    newBorrowerData[baseFieldName] = value;
                }
            });



            // Update borrowerData state
            updateBorrowerData(selectedBorrower, newBorrowerData);
        }
    };

    const handleSubmitDraftNoteForm = () => {
        if (!draftNoteContent) return;

        const lines = draftNoteContent.split("\n");
        const pairValues = [];

        lines.forEach((line) => {
            const [label, value] = line.split(":");
            if (label && value) {
                pairValues.push({
                    label: label.trim(),
                    value: value.trim(),
                });
            }
        });

        console.log("Note Content:", pairValues);

        if (pairValues.length > 0) {
            // Check if Names field contains comma-separated values
            const namesPair = pairValues.find(pair => pair.label === "Name");
            let borrowerNames = [];
            let newBorrowers = [...borrowers];
            let newBorrowerData = { ...borrowerData };
            
            if (namesPair && namesPair.value.includes(',')) {
                borrowerNames = namesPair.value.split(',').map(name => name.trim());
                
                // Create additional borrowers if needed
                while (newBorrowers.length < borrowerNames.length) {
                    const newBorrowerId = Math.max(...newBorrowers.map((b) => b.id)) + 1;
                    newBorrowers.push({ id: newBorrowerId });
                    newBorrowerData[newBorrowerId] = {};
                }
                
                // Update state with new borrowers
                setBorrowers(newBorrowers);
                setBorrowerData(newBorrowerData);
            }

            // Use setTimeout to ensure the form is ready before setting values
            setTimeout(() => {
                // Create a mapping between note labels and form field names
                const fieldMapping = {
                    "Unit Number": "unitNumber",
                    "Property Agent": "propertyAgent",
                    "SPA Price": "spaPrice",
                    "Name": "borrowerName" // Will be handled specially for multiple names
                };

                // Process each pair and set the corresponding form value
                pairValues.forEach((pair) => {
                    const fieldName = fieldMapping[pair.label];
                    if (fieldName) {
                        // Handle special case for Names with comma separation
                        if (fieldName === "borrowerName" && borrowerNames.length > 0) {
                            // Set name for each borrower
                            borrowerNames.forEach((name, index) => {
                                const borrowerId = newBorrowers[index]?.id || (index + 1);
                                const borrowerFieldName = `borrowerName_${borrowerId}`;
                                setValue(borrowerFieldName, name);
                                
                                // Update borrower data
                                updateBorrowerData(borrowerId, { borrowerName: name });
                            });
                        } else if (fieldName === "borrowerName") {
                            // Single name case
                            setValue("borrowerName_1", pair.value);
                            updateBorrowerData(1, { borrowerName: pair.value });
                        } else if (fieldName === "salesType") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "applyFor") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "flexiType") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "propertyType") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "completionStatus") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "bumiStatus") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "propertyHold") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (
                            fieldName === "project" ||
                            fieldName === "agency"
                        ) {
                            // For project and agency, we need to search for the matching option
                            // For now, just set the label and let the user select from dropdown
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value,
                            });
                        } else if (
                            ["spaPrice", "loanAmount", "marketValue"].includes(
                                fieldName
                            )
                        ) {
                            // Remove any currency symbols and convert to number
                            const cleanValue = pair.value.replace(
                                /[^0-9.]/g,
                                ""
                            );

                            setValue(fieldName, parseFloat(cleanValue));
                        } else if (
                            [
                                "marginRequested",
                                "loanTenure",
                                "landsize",
                            ].includes(fieldName)
                        ) {
                            // Convert to number
                            const cleanValue = pair.value.replace(
                                /[^0-9.]/g,
                                ""
                            );
                            setValue(fieldName, parseFloat(cleanValue));
                        } else if (
                            [
                                "caseDraftDate",
                                "submissionDate",
                                "spaDate",
                                "LADate",
                                "LODate",
                            ].includes(fieldName)
                        ) {
                            // Handle date format
                            const date = moment(
                                pair.value,
                                "DD-MM-YYYY"
                            ).format("YYYY-MM-DD");
                            if (date !== "Invalid date") {
                                setValue(fieldName, date);
                            }
                        } else {
                            setValue(fieldName, pair.value);
                        }
                    }
                });
            }, 300); // Give the form 300ms to fully render
        }

        setDraftNoteForm(false);
        setDraftNoteContent(""); // Clear the textarea after submission
    };

    // Modify handleCreateCase to use borrowerData
    const handleCreateCase = async (formData) => {
        try {
            const formValues = getValues();

            const requiredFields = {
                agency: 'Agency',
                project: 'Project', 
                mdgAgent: 'NFC Agent',
                propertyAgent: 'Property Agent'
            };

            const missingFields = Object.entries(requiredFields)
                .filter(([key]) => !formValues[key])
                .map(([,label]) => label);

            // Only the currently selected borrower's inputs are mounted, so
            // form values for other borrowers are unavailable. Read from
            // borrowerData (persistent) and overlay the live form values for
            // the currently selected borrower so the latest typed-in values
            // (not yet flushed to borrowerData) are picked up too.
            const getBorrowerData = (borrowerId) => {
                const persisted = borrowerData[borrowerId] || {};
                if (borrowerId !== selectedBorrower) return persisted;

                const fieldNames = [
                    "borrowerName", "borrowerNric", "borrowerContact",
                    "borrowerEmail", "borrowerEducationLevel", "borrowerSmoking",
                    "borrowerMotherName", "borrowerLengthOfStay",
                    "borrowerMailingAddress", "borrowerAllDetails", "borrowerSalary",
                    "borrowerMaritalStatus", "spouseName", "spouseNRIC",
                    "spouseContact", "spouseOccupation", "spouseCompanyName",
                    "spouseMonthlyIncome", "childrensNo", "currentLengthOfService",
                    "borrowerCurrentCompanyName", "borrowerCurrentCompanyAddress",
                    "borrowerCurrentNatureBusiness", "borrowerCurrentHRDirectline",
                    "borrowerPosition", "borrowerPastCompanyName",
                    "borrowerPastCompanyAddress", "borrowerPastNatureBusiness",
                    "borrowerPastHRDirectline", "borrowerPastPosition",
                    "borrowerPastYearService", "emergencyName", "emergencyContact",
                    "emergencyRelationship", "emergencyAddress", "emergencyIC",
                    "emergencyOccupation",
                ];
                const liveValues = {};
                fieldNames.forEach((name) => {
                    const v = formValues[`${name}_${borrowerId}`];
                    if (v !== undefined) liveValues[name] = v;
                });
                return { ...persisted, ...liveValues };
            };

            // Validate salary is filled for each borrower
            const borrowersMissingSalary = borrowers.filter((b) => {
                const borrower = getBorrowerData(b.id);
                const salary = borrower.borrowerSalary;
                return salary === undefined || salary === null || salary === '';
            });
            const borrowersMissingName = borrowers.filter((b) => {
                const borrower = getBorrowerData(b.id);
                const name = borrower.borrowerName;
                return name === undefined || name === null || name === "";
            });

            const combinedMissingFields = [...missingFields];
            if (borrowersMissingSalary.length > 0) {
                combinedMissingFields.push("Salary (RM) for all borrowers");
            }
            if (borrowersMissingName.length > 0) {
                combinedMissingFields.push("Borrower Name for all borrowers");
            }

            if (combinedMissingFields.length > 0) {
                console.log("Form Values:", formValues);
                alert(
                    `The following fields are required: ${combinedMissingFields.join(
                        ", "
                    )}`
                );
                return;
            }

            // Convert borrowerData to the format expected by the API
            const borrowerArray = borrowers.map((b) => {
                const borrower = getBorrowerData(b.id);

                return {
                    name: borrower.borrowerName,
                    ic: borrower.borrowerNric,
                    contact: borrower.borrowerContact,
                    email: borrower.borrowerEmail,
                    educationLevel: borrower.borrowerEducationLevel,
                    smoking: borrower.borrowerSmoking?.value,
                    motherName: borrower.borrowerMotherName,
                    maritalStatus: borrower.borrowerMaritalStatus?.value,
                    mailingAddress: borrower.borrowerMailingAddress,
                    allDetails: borrower.borrowerAllDetails,
                    salary: borrower.borrowerSalary
                        ? borrower.borrowerSalary.toString()
                        : undefined,
                    currentCompanyName: borrower.borrowerCurrentCompanyName,
                    currentCompanyAddress:
                        borrower.borrowerCurrentCompanyAddress,
                    currentNatureBusiness:
                        borrower.borrowerCurrentNatureBusiness,
                    currentHRDirectline: borrower.borrowerCurrentHRDirectline,
                    currentPosition: borrower.borrowerPosition,
                    currentYearService: borrower.currentLengthOfService ? borrower.currentLengthOfService.toString() : undefined,
                    pastCompanyName: borrower.borrowerPastCompanyName,
                    pastPosition: borrower.borrowerPastPosition,
                    pastYearService: borrower.borrowerPastYearService ? borrower.borrowerPastYearService.toString() : undefined,
                    pastHRDirectline: borrower.borrowerPastHRDirectline,
                    pastCompanyAddress: borrower.borrowerPastCompanyAddress,
                    pastNatureBusiness: borrower.borrowerPastNatureBusiness,
                    emergencyName: borrower.emergencyName,
                    emergencyIC: borrower.emergencyIC,
                    emergencyOccupation: borrower.emergencyOccupation,
                    emergencyContact: borrower.emergencyContact,
                    emergencyRelationship: borrower.emergencyRelationship,
                    emergencyAddress: borrower.emergencyAddress,
                    spouseName: borrower.spouseName,
                    spouseContact: borrower.spouseContact,
                    spouseNRIC: borrower.spouseNRIC,
                    lengthOfStay: borrower.borrowerLengthOfStay,
                    spouseOccupation: borrower.spouseOccupation,
                    spouseCompanyName: borrower.spouseCompanyName,
                    spouseMonthlyIncome: borrower.spouseMonthlyIncome
                        ? borrower.spouseMonthlyIncome.toString()
                        : undefined,
                    childrensNo: borrower.childrensNo,
                };
            });

            const result = await createCase({
                variables: {
                    project: formValues.project
                        ? formValues.project.value
                        : undefined,
                    agency: formValues.agency
                        ? formValues.agency.value
                        : undefined,
                    applyFor: formValues.applyFor
                        ? formValues.applyFor.value
                        : undefined,
                    flexiType: formValues.flexiType
                        ? formValues.flexiType.value === "full"
                            ? "full"
                            : "semi"
                        : undefined,
                    spaName: lawyerFirms,
                    spaLawyer: formValues.spaLawyer || undefined,
                    laLawyer: formValues.laLawyer || undefined,
                    valuerFirm: formValues.valuerFirm,
                    valuerName: formValues.valuerName,
                    marketValue: formValues.marketValue
                        ? (formValues.marketValue).toString()
                        : undefined,
                    submissionDate: formValues.submissionDate,
                    createdAt: new Date(),
                    status: "draft",
                    unitNumber: formValues.unitNumber,
                    state: formValues.state
                        ? formValues.state.value
                        : undefined,
                  
                        
                    mdgAgent: formValues.mdgAgent
                        ? formValues.mdgAgent.value 
                        : undefined,
                    nfcAgentOptional: formValues.mdgAgent
                        ? formValues.mdgAgent.value !== user.auth.userId
                            ? user.auth.userId
                            : undefined
                        : undefined,
                    salesType: formValues.salesType
                        ? formValues.salesType.value
                        : undefined,
                    lawyerRemarks: formValues.remarks,
                    propertyAgent: formValues.propertyAgent,
                    propertyDetails: {
                        address: formValues.propertyAddress,
                        propertyType: formValues.propertyType
                            ? formValues.propertyType.value
                            : undefined,
                        bumi: formValues.bumiStatus
                            ? formValues.bumiStatus.value === "bumi"
                                ? "bumi"
                                : formValues.bumiStatus.value === "malay"
                                ? "malay"
                                : "nonbumi"
                            : undefined,
                        hold: formValues.propertyHold
                            ? formValues.propertyHold.value
                            : undefined,
                        buildup: formValues.propertySize,
                        landsize: formValues.landsize,
                        renodetails: formValues.propertyRenodetails,
                        spaPrice: formValues.spaPrice
                            ? (formValues.spaPrice).toString()
                            : undefined,
                        margin: formValues.marginRequested
                            ? (formValues.marginRequested)
                            : undefined,
                        loanAmount: formValues.loanAmount
                            ? (formValues.loanAmount).toString()
                            : undefined,
                        loanTenure: formValues.loanTenure,
                        completed: formValues.completionStatus
                            ? formValues.completionStatus.value === "completed"
                            : false,
                    },
                    borrower: borrowerArray,
                },
            });

            const newCaseId = result?.data?.createCase?._id;
            if (newCaseId) {
                history.push(`/cases/edit/${newCaseId}`);
                alert("Case created successfully");
            }
        } catch (err) {
            alert(err.message || "Failed to create case");
        }
    };

    // Add useEffect to handle borrower switching
    useEffect(() => {
        // Get the saved data for the selected borrower
        const savedData = borrowerData[selectedBorrower] || {};

        // Map through all possible borrower fields and set their values
        Object.entries(savedData).forEach(([fieldName, value]) => {
            const formFieldName = `${fieldName}_${selectedBorrower}`;
            setValue(formFieldName, value);
        });
    }, [selectedBorrower, setValue, borrowerData]);

    // Function to handle field changes and update borrowerData
    const handleFieldChange = (fieldName, value, borrowerId) => {
        if (borrowerId) {
            updateBorrowerData(borrowerId, { [fieldName]: value });
        }
    };

    // Modify the renderFormField function to use defaultValue from borrowerData
    const renderFormField = (item, borrowerId) => {
        const fieldName = borrowerId ? `${item.name}_${borrowerId}` : item.name;
        const savedValue = borrowerId
            ? borrowerData[borrowerId]?.[item.name]
            : null;

        return (
            <div key={fieldName} className="mb-3">
                <div className="mb-1 text-sm text-lynch font-medium">
                    {item.label}
                </div>
                {item.type === "dynamicArray" ? (
                    <div className="space-y-2">
                        {lawyerFirms.map((value, index) => (
                            <div key={index} className="space-y-2">
                                <input
                                    className={`p-2 w-full rounded-sm block bg-white border text-sm font-normal ${
                                        errors[fieldName] ? "border-red-400" : "border-gray-300"
                                    }`}
                                    value={value}
                                    onChange={(e) => updateLawyerFirm(index, e.target.value)}
                                    placeholder={`Enter ${item.label}`}
                                    type="text"
                                />
                                <button
                                    type="button"
                                    className="px-3 py-2 bg-black text-white rounded hover:bg-blue-400 hover:text-black"
                                    onClick={() => removeLawyerFirm(index)}
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            className="px-3 py-2 bg-black text-white rounded hover:bg-blue-400 hover:text-black"
                            onClick={addLawyerFirm}
                        >
                            Add {item.label}
                        </button>
                    </div>
                ) : item.type === "multitext" ? (
                    <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                            <input
                                className={`p-2 flex-1 rounded-sm block bg-white border text-sm font-normal ${
                                    errors[fieldName] ? "border-red-400" : "border-gray-300"
                                }`}
                                name={fieldName}
                                ref={register({
                                    required: item.required ? `${item.label} is required` : false,
                                })}
                                defaultValue={savedValue}
                                placeholder={`Enter ${item.label} (separate multiple entries with ||)`}
                                type="text"
                                                            onChange={(e) => {
                                if (borrowerId) {
                                    handleFieldChange(
                                        item.name,
                                        e.target.value,
                                        borrowerId
                                    );
                                    // Also update the form field value
                                    setValue(fieldName, e.target.value);
                                }
                            }}
                            />
                            <button
                                type="button"
                                className="px-3 py-2 bg-black text-white rounded hover:bg-blue-400 hover:text-black"
                                onClick={() => {
                                    const currentValue = getValues(fieldName) || "";
                                    if (currentValue.trim()) {
                                        setValue(fieldName, currentValue + (currentValue.endsWith("||") ? "" : "||"));
                                    }
                                }}
                            >
                                Add More
                            </button>
                        </div>
                        <div className="text-xs text-gray-500">
                            {item.helperText || "Separate multiple entries with || or use the 'Add More' button"}
                        </div>
                    </div>
                ) : item.type === "select" ? (
                    <Controller
                        as={
                            <Select
                                styles={customStyles}
                                isSearchable={
                                    item.isSearchable || 
                                    item.name === "salesAgent" ||
                                    item.name === "pic"
                                }
                                options={item.options || []}
                                onChange={(selectedOption) => {
                                    if (borrowerId) {
                                        handleFieldChange(
                                            item.name,
                                            selectedOption,
                                            borrowerId
                                        );
                                        // Also update the form field value
                                        setValue(fieldName, selectedOption);
                                    }
                                }}
                            />
                        }
                        name={fieldName}
                        control={control}
                        defaultValue={savedValue}
                        rules={{ required: false }}
                        placeholder={item.label}
                    />
                ) : item.type === "multiselect" ? (
                    <Controller
                        as={
                            <Select
                                styles={customStyles}
                                isSearchable={true}
                                isMulti={true}
                                onChange={(selectedOptions) => {
                                    if (borrowerId) {
                                        handleFieldChange(
                                            item.name,
                                            selectedOptions,
                                            borrowerId
                                        );
                                        // Also update the form field value
                                        setValue(fieldName, selectedOptions);
                                    }
                                }}
                            />
                        }
                        name={fieldName}
                        control={control}
                        defaultValue={savedValue}
                        rules={{ required: false }}
                        placeholder={item.label}
                    />
                ) : item.type === "asyncselect" ? (
                    <Controller
                        as={
                            <AsyncSelect
                                styles={customStyles}
                                isSearchable={true}
                                onChange={(selectedOption) => {
                                    if (borrowerId) {
                                        handleFieldChange(
                                            item.name,
                                            selectedOption,
                                            borrowerId
                                        );
                                        // Also update the form field value
                                        setValue(fieldName, selectedOption);
                                    }
                                }}
                            />
                        }
                        name={fieldName}
                        control={control}
                        defaultValue={savedValue}
                        rules={{ required: false }}
                        placeholder={item.label}
                        loadOptions={
                            item.loadOptions === "loadProjects"
                                ? loadProjects
                                : item.loadOptions === "loadAgencies"
                                ? loadAgencies
                                : loadActiveUsers
                        }
                    />
                ) : item.type === "textarea" ? (
                    <textarea
                        className={`p-2 w-full rounded-sm block bg-white border text-sm font-normal ${
                            errors[fieldName]
                                ? "border-red-400"
                                : "border-gray-300"
                        }`}
                        name={fieldName}
                        ref={register({
                            required: false,
                        })}
                        defaultValue={savedValue}
                        placeholder={item.label}
                        rows="3"
                        onChange={(e) => {
                            if (borrowerId) {
                                handleFieldChange(
                                    item.name,
                                    e.target.value,
                                    borrowerId
                                );
                                // Also update the form field value
                                setValue(fieldName, e.target.value);
                            }
                        }}
                    />
                ) : item.type === "checkbox" ? (
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            name={fieldName}
                            ref={register}
                            defaultChecked={savedValue}
                            className="form-checkbox h-4 w-4 text-blue-600"
                            onChange={(e) => {
                                if (borrowerId) {
                                    handleFieldChange(
                                        item.name,
                                        e.target.checked,
                                        borrowerId
                                    );
                                    // Also update the form field value
                                    setValue(fieldName, e.target.checked);
                                }
                            }}
                        />
                        <span className="ml-2 text-sm text-lynch font-medium">
                            {item.label}
                        </span>
                    </label>
                ) : (
                    <input
                        className={`p-2 w-full rounded-sm block bg-white border text-sm font-normal ${
                            errors[fieldName]
                                ? "border-red-400"
                                : "border-gray-300"
                        }`}
                        name={fieldName}
                        ref={register({
                            required: false,
                        })}
                        defaultValue={savedValue}
                        placeholder={item.label}
                            type={item.type || "text"}
                            step={item.type === "number" ? "0.01" : undefined}
                        onChange={(e) => {
                            if (borrowerId) {
                                handleFieldChange(
                                    item.name,
                                    e.target.value,
                                    borrowerId
                                );
                                // Also update the form field value
                                setValue(fieldName, e.target.value);
                            }
                        }}
                    />
                )}
                {errors[fieldName] && (
                    <p className="text-red-400 text-xs">{`${item.label} cannot be empty`}</p>
                )}
            </div>
        );
    };

    // Update the handleSubmitCreateNoteForm function
    const handleSubmitCreateNoteForm = () => {
        if (!noteContent) return;

        const lines = noteContent.split("\n");
        const pairValues = [];

        lines.forEach((line) => {
            const [label, value] = line.split(":");
            if (label && value) {
                pairValues.push({
                    label: label.trim(),
                    value: value.trim(),
                });
            }
        });

        console.log("Note Content:", pairValues);

        if (pairValues.length > 0) {
            // Open the Add Borrower form

            // Use setTimeout to ensure the form is ready before setting values
            setTimeout(() => {
                // Create a mapping between note labels and form field names
                const fieldMapping = {
                    "Case Draft Date": "caseDraftDate",
                    Project: "project",
                    "Sales Type": "salesType",
                    "Apply For": "applyFor",
                    "Flexi Type": "flexiType",
                    "Unit Number": "unitNumber",
                    "Full Property Address": "propertyAddress",
                    "Renovation Details": "propertyRenodetails",
                    "Project Name": "projectName",
                    "Property Type": "propertyType",
                    Completed: "completionStatus",
                    "Bumi/Non-Bumi": "bumiStatus",
                    "Freehold/Leasehold": "propertyHold",
                    "Land Size": "landsize",
                    "Build Up": "propertySize",
                    "SPA Price(RM)": "spaPrice",
                    "Margin Requested": "marginRequested",
                    "Loan Amount(RM)": "loanAmount",
                    "Loan Tenure": "loanTenure",
                    Remarks: "remarks",
                    "Property Agent": "propertyAgent",
                    Agency: "agency",
                    "SPA Date": "spaDate",
                    "LA Date": "LADate",
                    "SignedLo Date": "LODate",
                    "Valuer Name": "valuerName",
                    "Valuation Firm": "valuerFirm",
                    "Market Value(RM)": "marketValue",
                    "SPA Name(Borrower)": "spaName",
                    "SPA Lawyer Firm": "spaLawyer",
                    "LA Lawyer Firm": "laLawyer",
                };

                // Process each pair and set the corresponding form value
                pairValues.forEach((pair) => {
                    const fieldName = fieldMapping[pair.label];
                    if (fieldName) {
                        // Handle special case for SPA Name (dynamic array)
                        if (fieldName === "spaName") {
                            // Set the SPA Name as the first item in the lawyerFirms array
                            setLawyerFirms([pair.value, ...lawyerFirms.slice(1)]);
                        } else if (fieldName === "salesType") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "applyFor") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "flexiType") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "propertyType") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "completionStatus") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "bumiStatus") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (fieldName === "propertyHold") {
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value.toLowerCase(),
                            });
                        } else if (
                            fieldName === "project" ||
                            fieldName === "agency"
                        ) {
                            // For project and agency, we need to search for the matching option
                            // For now, just set the label and let the user select from dropdown
                            setValue(fieldName, {
                                label: pair.value,
                                value: pair.value,
                            });
                        } else if (
                            ["spaPrice", "loanAmount", "marketValue"].includes(
                                fieldName
                            )
                        ) {
                            // Remove any currency symbols and convert to number
                            const cleanValue = pair.value.replace(
                                /[^0-9.]/g,
                                ""
                            );

                            setValue(fieldName, parseFloat(cleanValue));
                        } else if (
                            [
                                "marginRequested",
                                "loanTenure",
                                "landsize",
                            ].includes(fieldName)
                        ) {
                            // Convert to number
                            const cleanValue = pair.value.replace(
                                /[^0-9.]/g,
                                ""
                            );
                            setValue(fieldName, parseFloat(cleanValue));
                        } else if (
                            [
                                "caseDraftDate",
                                "submissionDate",
                                "spaDate",
                                "LADate",
                                "LODate",
                            ].includes(fieldName)
                        ) {
                            // Handle date format
                            const date = moment(
                                pair.value,
                                "DD-MM-YYYY"
                            ).format("YYYY-MM-DD");
                            if (date !== "Invalid date") {
                                setValue(fieldName, date);
                            }
                        } else {
                            setValue(fieldName, pair.value);
                        }
                    }
                });
            }, 300); // Give the form 300ms to fully render
        }

        setCreateNoteForm(false);
        setNoteContent(""); // Clear the textarea after submission
    };


    return (
        <div className="py-4 px-4 md:px-12">
            {/* Header Actions */}
            <div className="flex justify-between items-center mb-6">
            <button
                    onClick={() => history.push("/cases")}
                    className="px-4 py-2 text-white bg-black rounded-lg hover:bg-blue-400 hover:text-black transition-colors duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L4.414 9H17a1 1 0 110 2H4.414l5.293 5.293a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                    <span>Back To List</span>
                </button>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-lg shadow">
                {activeTab === "caseInfo" && (
                    <div className="p-6">
                        <form onSubmit={handleSubmit(handleCreateCase)}>
                            {/* Case Information Section */}
                            <div className="mb-6">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-orange-400 text-white p-2 mb-4">
                                    <h3 className="text-base font-medium mb-2 md:mb-0">
                                        Case Information
                                    </h3>
                                    <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2">
                                        <button
                                            type="button"
                                            className="px-4 py-2 text-white bg-black rounded-lg hover:bg-blue-400 hover:text-black transition-colors duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
                                            onClick={() => setCreateNoteForm(true)}
                                        >
                                            <FiFileText className="w-4 h-4 mr-1" />
                                            <span>Auto Fill</span>
                                        </button>
                                        <button
                                            type="button"
                                            className="px-4 py-2 text-white bg-black rounded-lg hover:bg-blue-400 hover:text-black transition-colors duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
                                            onClick={() => setDraftNoteForm(true)}
                                        >
                                            <FiFileText className="w-4 h-4 mr-1" />
                                            <span>Draft Fill</span>
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {createformFields
                                        .filter(
                                            (field) =>
                                                field.section ===
                                                "Case Information"
                                        )
                                        .map((item) => renderFormField(item))}
                                </div>
                            </div>

                             {/* Person In Charge Section */}
                             <div className="mb-6">
                                <div className="flex items-center bg-orange-400 text-white p-2 mb-4">
                                    <h3 className="text-base font-medium">
                                        Person In Charge
                                    </h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    
                                    {createformFields
                                        .filter(
                                            (field) =>
                                                field.section ===
                                                "Person In Charge"
                                        )
                                        .map((item) => renderFormField(item))}
                                                        <div className="w-full mb-3">
                                        <div className="mb-1 text-sm text-lynch font-medium">
                                            NFC Agent
                                        </div>
                                        <Controller
                                            as={
                                                <AsyncSelect
                                                    styles={customStyles}
                                                />
                                            }
                                            name="mdgAgent"
                                            control={control}
                                            rules={{ required: false }}
                                            loadOptions={loadActiveUsers}
                                        />
                                        {errors.mdgAgent && (
                                            <p className="text-red-400 text-xs">
                                                NFC Agent cannot be empty
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Property Loan Details Section */}
                            <div className="mb-6">
                                <div className="bg-orange-400 text-white p-2 mb-4">
                                    <h3 className="text-base font-medium">
                                        Property Loan Details
                                    </h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {createformFields
                                        .filter(
                                            (field) =>
                                                field.section ===
                                                "Property Loan Details"
                                        )
                                        .map((item) => renderFormField(item))}
                                </div>
                            </div>

                            {/* Borrower Information Section */}
                            <div className="mb-6">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-orange-400 text-white p-2 mb-4">
                                    <div className="flex items-center gap-3 mb-2 md:mb-0">
                                        <h3 className="text-base font-medium">
                                            Borrower Information
                                        </h3>
                                        <button
                                            type="button"
                                            className={`px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide border transition-all duration-200 ${
                                                isAllDetailsMode
                                                    ? "bg-teal-600 border-teal-600 text-white shadow-sm hover:bg-teal-700"
                                                    : "bg-yellow-100 border-yellow-300 text-gray-800 hover:bg-yellow-200"
                                            }`}
                                            onClick={() =>
                                                setIsAllDetailsMode((prev) => !prev)
                                            }
                                        >
                                            {isAllDetailsMode
                                                ? "All Details Mode: ON"
                                                : "All Details Mode: OFF"}
                                        </button>
                                    </div>
                                    <div className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-4">
                                        <button
                                            type="button"
                                         className="px-4 py-2 text-white bg-black rounded-lg hover:bg-blue-400 hover:text-black transition-colors duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
                                            onClick={() =>
                                                setBorrowerNoteForm(true)
                                            }
                                        >
                                            <FiFileText className="w-4 h-4 mr-1" />
                                            <span>Auto Fill</span>
                                        </button>
                                        <div className="flex flex-wrap gap-2">
                                            {borrowers.map((borrower) => (
                                                <button
                                                    key={borrower.id}
                                                    type="button"
                                                    className={`px-3 py-1 rounded-lg text-sm  ${
                                                        selectedBorrower ===
                                                        borrower.id
                                                            ? "bg-indigo-700 text-white shadow-md"
                                                            : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                                                    }`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleBorrowerTabClick(borrower.id);
                                                    }}
                                                >
                                                    Borrower #{borrower.id}
                                                    {borrowers.length > 1 && (
                                                        <span
                                                            className="ml-2 hover:text-red-300"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveBorrower(
                                                                    borrower.id
                                                                );
                                                            }}
                                                        >
                                                            ×
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                            <button
                                                type="button"
                                              className="px-4 py-2 text-white bg-black rounded-lg hover:bg-blue-400 hover:text-black transition-colors duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
                                                onClick={handleAddBorrower}
                                            >
                                                Add Borrower
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {createformFields
                                        .filter(
                                            (field) =>
                                                field.section === "Borrower Information" &&
                                                (isAllDetailsMode
                                                    ? [
                                                          "borrowerSalary",
                                                          "borrowerName",
                                                          "borrowerAllDetails",
                                                      ].includes(field.name)
                                                    : field.name !==
                                                      "borrowerAllDetails")
                                        )
                                        .map((item) =>
                                            renderFormField(
                                                item,
                                                selectedBorrower
                                            )
                                        )}
                                </div>
                            </div>

                            {!isAllDetailsMode && (
                                <>
                                    {/* Current Employment Details Section */}
                                    <div className="mb-6">
                                        <div className="flex items-center bg-red-500 text-white p-2 mb-4">
                                            <h3 className="text-base font-medium">
                                                Current Employment Details
                                            </h3>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {createformFields
                                                .filter(
                                                    (field) =>
                                                        field.section ===
                                                        "Current Employment Details"
                                                )
                                                .map((item) =>
                                                    renderFormField(
                                                        item,
                                                        selectedBorrower
                                                    )
                                                )}
                                        </div>
                                    </div>

                                    {/* Previous Employment Status Section */}
                                    <div className="mb-6">
                                        <div className="flex items-center bg-red-500 text-white p-2 mb-4">
                                            <h3 className="text-base font-medium">
                                                Previous Employment Status
                                            </h3>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {createformFields
                                                .filter(
                                                    (field) =>
                                                        field.section ===
                                                        "Previous Employment Status"
                                                )
                                                .map((item) =>
                                                    renderFormField(
                                                        item,
                                                        selectedBorrower
                                                    )
                                                )}
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Emergency Contact Section */}
                            <div className="mb-6">
                                <div className="flex items-center bg-orange-400 text-white p-2 mb-4">
                                    <h3 className="text-base font-medium">
                                        Emergency Contact
                                    </h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {createformFields
                                        .filter(
                                            (field) =>
                                                field.section ===
                                                "Emergency Contact"
                                        )
                                        .map((item) =>
                                            renderFormField(
                                                item,
                                                selectedBorrower
                                            )
                                        )}
                                </div>
                            </div>

 {/* Lawyer Firm Section */}
 <div className="mb-6">
                                <div className="flex items-center bg-orange-400 text-white p-2 mb-4">
                                    <h3 className="text-base font-medium">
                                        Lawyer Firm
                                    </h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {createformFields
                                        .filter(
                                            (field) =>
                                                field.section === "Lawyer Firm"
                                        )
                                        .map((item) => renderFormField(item))}
                                </div>
                            </div>

                            {/* Remarks Section */}
                            <div className="mb-6">
                                <div className="flex items-center bg-orange-400 text-white p-2 mb-4">
                                    <h3 className="text-base font-medium">
                                        Remarks
                                    </h3>
                                </div>
                                <div className="grid grid-cols-1 gap-4">
                                    {createformFields
                                        .filter(
                                            (field) =>
                                                field.section === "Remarks"
                                        )
                                        .map((item) => renderFormField(item))}
                                </div>
                            </div>

                           

                            {/* Footer Buttons */}
                            <div className="flex flex-col md:flex-row justify-between mt-8 gap-4">
                                <button
                                    type="button"
                                    onClick={() => history.push("/cases")}
                                    className="px-8 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    className="px-8 py-2 bg-black text-white rounded-lg hover:bg-blue-400 hover:text-black"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {activeTab === "caseLogs" && (
                    <div className="p-6">
                        <h2 className="text-xl font-semibold mb-4">
                            Case Logs
                        </h2>
                        <p className="text-gray-500">
                            No logs available for new case
                        </p>
                    </div>
                )}

                {activeTab === "documents" && (
                    <div className="p-6">
                        <h2 className="text-xl font-semibold mb-4">
                            Documents
                        </h2>
                        <p className="text-gray-500">
                            No documents available for new case
                        </p>
                    </div>
                )}

                {activeTab === "banks" && (
                    <div className="p-6">
                        <h2 className="text-xl font-semibold mb-4">Banks</h2>
                        <p className="text-gray-500">
                            No banks available for new case
                        </p>
                    </div>
                )}

                {activeTab === "mailHistory" && (
                    <div className="p-6">
                        <h2 className="text-xl font-semibold mb-4">
                            Mail History
                        </h2>
                        <p className="text-gray-500">
                            No mail history available for new case
                        </p>
                    </div>
                )}
            </div>

            {/* Add the modal component at the end of your return statement, before the closing tags */}
            {createNoteForm && (
                <Modal reset={resetCreateNoteForm}>
                    <div className="container h-full mx-auto flex justify-center items-center">
                        <div className="panel bg-white p-6 rounded-md max-w-4xl w-full">
                            <div className="flex justify-between items-center mb-4">
                                <div className="text-2xl font-bold">
                                    Auto Fill Form
                                </div>
                                <button
                                    onClick={() =>
                                        setShowTemplate(!showTemplate)
                                    }
                                    className="px-4 py-0 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                >
                                    {showTemplate
                                        ? "Show Form"
                                        : "Show Template"}
                                </button>
                            </div>

                            {!showTemplate ? (
                                <div className="w-full">
                                    <textarea
                                        value={noteContent}
                                        onChange={(e) =>
                                            setNoteContent(e.target.value)
                                        }
                                        className="w-full h-half p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Paste your content here..."
                                    />
                                    <div className="flex justify-end mt-4">
                                        <button
                                            type="button"
                                            onClick={handleSubmitCreateNoteForm}
                                            className="px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-600"
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="w-full">
                                    <div className="mb-2 font-medium">
                                        Template Reference
                                    </div>
                                    <button
                                            onClick={() =>
                                                navigator.clipboard
                                                    .writeText(`Sales Type: Project
Apply For: Conventional
Flexi Type: Semi flexi
Unit Number: DEMO-C
Full Property Address: C3-3-13A, Solaris Dutamas, No.1, Jalan Dutamas 1,
Renovation Details: Kitchen Cabinet, Kitchen Tabletop, Jacuzzi
Project Name: 163 MK
Property Type: Apartment
Completed: Under Con
Bumi/Non-Bumi: Non Bumi
Freehold/Leasehold: Freehold
Land Size: 3,000 sqft
Build Up: 1,300 sqft
SPA Price(RM): 1,000,000
Margin Requested: 90%
Loan Amount(RM): 900,000
Loan Tenure: 35
Remarks: Good client speed up
Property Agent: Mr Loi
SPA Name(Borrower): John Doe
SPA Lawyer Firm: Mr Singh
LA Lawyer Firm: Alucard
Valuation Firm: Law Firm
Valuer Name: Kin Kooi Chin
Market Value(RM): 1,200,000`)
                                            }
                                            className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 w-full"
                                        >
                                            Copy Template
                                        </button>
                                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        <pre className="whitespace-pre-wrap text-sm text-gray-600">
                                            {`Sales Type: Project
Apply For: Conventional
Flexi Type: Semi flexi
Unit Number: DEMO-C
Full Property Address: C3-3-13A, Solaris Dutamas, No.1, Jalan Dutamas 1,
Renovation Details: Kitchen Cabinet, Kitchen Tabletop, Jacuzzi
Project Name: 163 MK
Property Type: Apartment
Completed: Under Con
Bumi/Non-Bumi: Non Bumi
Freehold/Leasehold: Freehold
Land Size: 3,000 sqft
Build Up: 1,300 sqft
SPA Price(RM): 1,000,000
Margin Requested: 90%
Loan Amount(RM): 900,000
Loan Tenure: 35
Remarks: Good client speed up
Property Agent: Mr Loi
SPA Name(Borrower): John Doe
SPA Lawyer Firm: Mr Singh
LA Lawyer Firm: Alucard
Valuation Firm: Law Firm
Valuer Name: Kin Kooi Chin
Market Value(RM): 1,200,000`}
                                        </pre>
                                    
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            )}

{draftNoteForm && (
                <Modal reset={resetDraftNoteForm}>
                    <div className="container h-full mx-auto flex justify-center items-center">
                        <div className="panel bg-white p-6 rounded-md max-w-4xl w-full">
                            <div className="flex justify-between items-center mb-4">
                                <div className="text-2xl font-bold">
                                    Draft Fill Form
                                </div>
                                <button
                                    onClick={() =>
                                        setShowTemplate(!showTemplate)
                                    }
                                    className="px-4 py-0 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                >
                                    {showTemplate
                                        ? "Show Form"
                                        : "Show Template"}
                                </button>
                            </div>

                            {!showTemplate ? (
                                <div className="w-full">
                                    <textarea
                                        value={draftNoteContent}
                                        onChange={(e) =>
                                            setDraftNoteContent(e.target.value)
                                        }
                                        className="w-full h-half p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Paste your content here..."
                                    />
                                    <div className="flex justify-end mt-4">
                                        <button
                                            type="button"
                                            onClick={handleSubmitDraftNoteForm}
                                            className="px-4 py-2 bg-orange-400 text-white rounded-lg hover:bg-orange-600"
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="w-full">
                                    <div className="mb-2 font-medium">
                                        Template Reference
                                    </div>
                                    <button
                                            onClick={() =>
                                                navigator.clipboard
                                                    .writeText(`Unit Number: DEMO-C
SPA Price: 1,000,000
Property Agent: Mr Loi
Name : John Doe, Mary Doe
`)
                                            }
                                            className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 w-full"
                                        >
                                            Copy Template
                                        </button>
                                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        <pre className="whitespace-pre-wrap text-sm text-gray-600">
                                            {`Unit Number: DEMO-C
SPA Price: 1,000,000
Property Agent: Mr Loi
Name : John Doe, Mary Doe`}
                                        </pre>
                                    
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Add this modal component near the end of the return statement, before the closing tags */}
            {borrowerNoteForm && (
                <Modal reset={resetBorrowerNoteForm}>
                    <div className="container h-full mx-auto flex justify-center items-center">
                        <div className="panel bg-white p-6 rounded-md max-w-4xl w-full">
                            <div className="flex justify-between items-center mb-4">
                                <div className="text-2xl font-bold">
                                    Auto Fill Borrower Form
                                </div>
                                <button
                                    onClick={() =>
                                        setShowTemplate(!showTemplate)
                                    }
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                >
                                    {showTemplate
                                        ? "Show Form"
                                        : "Show Template"}
                                </button>
                            </div>

                            {!showTemplate ? (
                                <div className="w-full">
                                    <textarea
                                        value={borrowerNoteContent}
                                        onChange={(e) =>
                                            setBorrowerNoteContent(
                                                e.target.value
                                            )
                                        }
                                        className="w-full h-half p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Paste your content here..."
                                    />
                                    <div className="flex justify-end mt-4">
                                        <button
                                            type="button"
                                            onClick={
                                                handleSubmitBorrowerNoteForm
                                            }
                                            className="px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-600"
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="w-full">
                                <div className="mb-2 font-medium">
                                    Template Reference
                                </div>
                                    <button
                                        onClick={() => navigator.clipboard.writeText(`NAME : John Doe
NRIC : S1234567A
CONTACT : +65 9123 4567
EMAIL ADDRESS : johndoe@example.com
EDUCATION LEVEL: Bachelor's Degree in Business Administration
SALARY: 5000
SMOKING/NO: No
MOTHER NAME: Mary Doe
MAILING ADDRESS : 123 Orchard Road, #12-34, Singapore 238567
LENGTH OF STAY In this MAILING ADDRESS: 8 years

CURRENT COMPANY NAME: ABC Solutions Pte Ltd
CURRENT COMPANY ADDRESS : 456 Clementi Road, Singapore 129567
CURRENT COMPANY TELEPHONE : +65 6123 4567
CURRENT POSITION: IT Analyst
CURRENT DATE OF JOINING: 06 June 2020
CURRENT NATURE OF BUSINESS : IT Consulting

PREVIOUS COMPANY NAME : XYZ Tech Pte Ltd
PREVIOUS ADDRESS : 789 Raffles Place, Singapore 048624
PREVIOUS HR DIRECT LINE : +65 6321 8765
PREVIOUS YRS IN SERVICE : 5 years
PREVIOUS POSITION : Project Manager
PREVIOUS NATURE OF BUSINESS : Technology Solutions

MARITAL STATUS: divorced
SPOUSE NAME : Jane Doe
SPOUSE IC : 204032104203
SPOUSE CONTACT : 012331231
SPOUSE OCCUPATION : Accountant
SPOUSE COMPANY NAME: ABC Solutions Pte Ltd
SPOUSE MONTHLY INCOME: 10000
NO. CHILDREN: 2

FULL NAME: Michael Doe
IC NUMBER: 123456789012
EMERGENCY CONTACT: +65 9123 6789
RELATIONSHIP: Brother
HOUSE ADDRESS: 890 Tanjong Pagar Road, #03-21, Singapore 089617
OCCUPATION: Software Engineer`)}
                                        className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 w-full"
                                    >
                                        Copy Template
                                    </button>
                                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        <pre className="whitespace-pre-wrap text-sm text-gray-600">
{`NAME : John Doe
NRIC : S1234567A
CONTACT : +65 9123 4567
EMAIL ADDRESS : johndoe@example.com
EDUCATION LEVEL: Bachelor's Degree in Business Administration
SALARY: 5000
SMOKING/NO: No
MOTHER NAME: Mary Doe
MAILING ADDRESS : 123 Orchard Road, #12-34, Singapore 238567
LENGTH OF STAY In this MAILING ADDRESS: 8 years

CURRENT COMPANY NAME: ABC Solutions Pte Ltd
CURRENT COMPANY ADDRESS : 456 Clementi Road, Singapore 129567
CURRENT COMPANY TELEPHONE : +65 6123 4567
CURRENT POSITION: IT Analyst
CURRENT DATE OF JOINING: 06 June 2020
CURRENT NATURE OF BUSINESS : IT Consulting

PREVIOUS COMPANY NAME : XYZ Tech Pte Ltd
PREVIOUS ADDRESS : 789 Raffles Place, Singapore 048624
PREVIOUS HR DIRECT LINE : +65 6321 8765
PREVIOUS YRS IN SERVICE : 5 years
PREVIOUS POSITION : Project Manager
PREVIOUS NATURE OF BUSINESS : Technology Solutions

MARITAL STATUS: divorced
SPOUSE NAME : Jane Doe
SPOUSE IC : 204032104203
SPOUSE CONTACT : 012331231
SPOUSE OCCUPATION : Accountant
SPOUSE COMPANY NAME: ABC Solutions Pte Ltd
SPOUSE MONTHLY INCOME: 10000
NO. CHILDREN: 2

FULL NAME: Michael Doe
IC NUMBER: 123456789012
EMERGENCY CONTACT: +65 9123 6789
RELATIONSHIP: Brother
HOUSE ADDRESS: 890 Tanjong Pagar Road, #03-21, Singapore 089617
OCCUPATION: Software Engineer`}
                                        </pre>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default withUser(NewCase);
